import { useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Button from '../button';
import { FormInputCheckbox, FormInputDropdown, FormInputText } from '../form';
import ApiDrFinder from '@/api-client/dr-finder';
import CustomImage from '../image';
import FindDoctorCard from './card';
import Modal from '../modal';
import { LayoutContext } from '../layout-context-container';
import DoctorFinderDirectoryModal from './directory-modal';

const filterSettingsInitialState = {
  publications: false,
  clinical_trials: false,
  ccc_coe: false,
};

/**
 * @typedef {Object} FilterAction
 * @property {string} type - Action type
 * @property {boolean} [publications] - Publications filter state
 * @property {boolean} [clinical_trials] - Clinical trials filter state
 * @property {boolean} [ccc_coe] - CCC/COE filter state
 */

/**
 * @param {typeof filterSettingsInitialState} state
 * @param {FilterAction} action
 * @returns {typeof filterSettingsInitialState}
 */
function filterSettingsReducer(state, action) {
  const { type, publications, clinical_trials, ccc_coe } = action;

  switch (type) {
    case 'SET_PUBLICATIONS':
      return { ...state, publications, };
    case 'SET_CLINICAL_TRIALS':
      return { ...state, clinical_trials, };
    case 'SET_CCC_COE':
      return { ...state, ccc_coe, };
    case 'SET':
      const actionCopy = {...action};
      delete actionCopy.type;
      return { ...state, ...actionCopy };
    case 'RESET':
      return filterSettingsInitialState;
    default:
      return state;
  }
}

export default function FindDoctorTool({
    children,
    instructions = null,
    isHcp = false,
  }) {
  const searchRef = useRef(null);
  const formRef = useRef(null);
  const modalRef = useRef(null);
  const directoryModalRef = useRef(null);
  const thankYouModalRef = useRef(null);
  const zipCodeRef = useRef(null);
  const radiusRef = useRef(null);
  const filterRadiusRef = useRef(null);
  const resultsRef = useRef(null);
  const hasPublicationsRef = useRef([]);
  const hasClinicalTrialsRef = useRef([]);
  const practiceCccCoeRef = useRef([]);
  const bottomRef = useRef(null);

  const initErrorMessage = <>Please enter a valid 5-digit US ZIP code</>;

  const [searching, setSearching] = useState(false);
  const [search, setSearch] = useState(false);
  /** @type {Array | null} */
  const [results, setResults] = useState(null);
  const [page, setPage] = useState(0); // zero means no page, first search
  const [pagination, setPagination] = useState(null);
  const [radius, setRadius] = useState(25); // Default radius in miles
  const [modalRadius, setModalRadius] = useState(radius); // Default radius in miles
  const [errorMessage, setErrorMessage] = useState(initErrorMessage);
  const [filterSettings, dispatchFilterSettings] = useReducer(filterSettingsReducer, filterSettingsInitialState);
  const [modalFilterSettings, dispatchModalFilterSettings] = useReducer(filterSettingsReducer, filterSettingsInitialState);
  const numSelectedFilters = useMemo(() => 1 + (filterSettings.publications ? 1 : 0) + (filterSettings.clinical_trials ? 1 : 0) + (filterSettings.ccc_coe ? 1 : 0), [filterSettings]);

  const { setAutoScrollActive } = useContext(LayoutContext);

  const radiusOptions = useMemo(() => [
    { key: 0, id: 'radius-25', code: '25', label: '25 miles' },
    { key: 1, id: 'radius-50', code: '50', label: '50 miles' },
    { key: 2, id: 'radius-100', code: '100', label: '100 miles' },
    { key: 3, id: 'radius-200', code: '200', label: '200 miles' },
  ], []);

  const filterRadiusOptions = useMemo(() => [
    { key: 0, id: 'filter-radius-25', code: '25', label: 'Radius 25 mi', itemLabel: '25 miles', rest: {
      "data-gtm-event-category": "Find a Doctor Tool",
      "data-gtm-event": "Click",
      "data-gtm-event-label": "Select Radius - 25 Miles"
    }},
    { key: 1, id: 'filter-radius-50', code: '50', label: 'Radius 50 mi', itemLabel: '50 miles', rest: {
      "data-gtm-event-category": "Find a Doctor Tool",
      "data-gtm-event": "Click",
      "data-gtm-event-label": "Select Radius - 50 Miles"
    }},
    { key: 2, id: 'filter-radius-100', code: '100', label: 'Radius 100 mi', itemLabel: '100 miles', rest: {
      "data-gtm-event-category": "Find a Doctor Tool",
      "data-gtm-event": "Click",
      "data-gtm-event-label": "Select Radius - 100 Miles"
    }},
    { key: 3, id: 'filter-radius-200', code: '200', label: 'Radius 200 mi', itemLabel: '200 miles', rest: {
      "data-gtm-event-category": "Find a Doctor Tool",
      "data-gtm-event": "Click",
      "data-gtm-event-label": "Select Radius - 200 Miles"
    }},
  ], []);

  function handleSearch(e) {
    e.preventDefault();

    if (searching) return; // Prevent multiple searches

    const valid = [
      zipCodeRef.current?.validate(),
    ].filter((v) => v !== true).length === 0;

    if (!valid) return;
    
    dispatchFilterSettings({ type: 'RESET' });
    setRadius(25);
    setSearching(true);
    setPage(0);
    setPagination(null);
    setSearch(true);
  }

  async function fetchResults() {
    const zip_code = zipCodeRef.current?.value?.trim();
    // eslint-disable-next-line no-unused-vars
    const data = {
      zip_code,
      page,
      r: radius || 25, // Default to 25 miles if not set
    };
    if (filterSettings.publications) {
      data.publications = filterSettings.publications ? 'TRUE' : 'FALSE';
    }
    if (filterSettings.clinical_trials) {
      data.clinical_trials = filterSettings.clinical_trials ? 'TRUE' : 'FALSE';
    }
    if (filterSettings.ccc_coe) {
      data.ccc_coe = filterSettings.ccc_coe ? 'TRUE' : 'FALSE';
    }

    try {
      if (!zip_code) {
        const error = new Error('VALIDATION_ERROR: Zip code is required');
        error['cause'] = {
          type: 'VALIDATION_ERROR',
          code: 'VALIDATION_ERROR_NO_ZIP_CODE'
        };
        throw error;
      }
      const result = await ApiDrFinder({
        formId: 'dr_finder_3000',
        data
      });

      // console.log('Search result:', result);

      if (result?.success && result?.response && radius) {
        if (result?.response?.pagination?.r.toString() !== radius.toString() && page === 0) {
          setRadius(result?.response?.pagination?.r);
        }
        if (page === 0) {
          setResults(result?.response?.data);
          setPage(1);
          setPagination(result?.response?.pagination);
          // console.log('Pagination:', result?.response?.pagination);
        } else {
          setResults((prev) => [...(prev || []), ...(result?.response?.data || [])]);
        }

        if (result?.response?.data && result?.response?.data.length === 0) {
          setErrorMessage(<>0 search results, please check your US ZIP code in the results</>);
          zipCodeRef.current?.setValid(false);
        }

        setSearching(false);
      } else {
        if (result?.error && result?.error?.cause?.type === 'GEOCODIO_ERROR' && result?.error?.message?.toLowerCase().includes('no matches found')) {
          zipCodeRef.current?.setValid(false);
        } else if (result?.error && result?.error?.cause?.type === 'GEOCODIO_ERROR' && !result?.error?.message?.toLowerCase().includes('no matches found')) {
          setErrorMessage(<>Geocoding service timeout. Please try again.</>);
          zipCodeRef.current?.setValid(false);
        } else if (process.env.NODE_ENV === 'development' && result?.error?.message) {
          // display full error message in development
          setErrorMessage(result?.error?.message);
          zipCodeRef.current?.setValid(false);
        }
        setResults(null);
        setPage(0);
        setPagination(null);
      }

    } catch (error) {
      console.log('Error during search: ' + error.message, error?.cause?.type);
      if (error?.cause?.code === 'NO_SERVER_ERROR_IN_DEV_MODE') {
        // show demo data
        /** @type {Object | null} */
        const devData = (await import('./dev-data')).default;
        setResults(devData?.data);
        setPagination(devData.pagination);
        setPage(1);
      } else {
        if (process.env.NODE_ENV === 'development') {
          // display full error message in development
          setErrorMessage(error?.message);
        } else {
          // use generic error message in production
          setErrorMessage(<>Geocoding service timeout. Please try again.</>);
        }
        zipCodeRef.current?.setValid(false);
      }
    } finally {
      setSearching(false);
    }
  }

  function handlePagination(e) {
    if (searching) return;

    const direction = e.currentTarget.dataset.direction;
    const totalPages = pagination.total;
    if (direction === 'load-more' && page < totalPages) {
      const rect = bottomRef.current?.getBoundingClientRect();
      const y = window.innerWidth < 700 ? rect.top + window.scrollY - 80 : rect.top + window.scrollY - 120;

      window.scrollTo({top: y, behavior: "smooth"});
      setPage(prevPage => prevPage + 1);

      setTimeout(() => {
        setSearch(true);
        setSearching(true);
      }, 500);
    }
  }

  function getDropdownIndexByCode(code, arr) {
    return arr.findIndex(option => option.code === code);
  }

  function resetDropdownFilter() {
    if (radiusRef.current && radius && radiusRef.current.code !== radius.toString()) {
      radiusRef.current?.setIndexValue(getDropdownIndexByCode(radius.toString(), radiusOptions));
    }
    if (filterRadiusRef.current && radius && filterRadiusRef.current.code !== radius.toString()) {
      filterRadiusRef.current?.setIndexValue(getDropdownIndexByCode(radius.toString(), filterRadiusOptions));
    }
  }

  function handleFilterOptions(e) {
    e?.preventDefault();
    if (searching) return;

    if (e) {
      const type = e.currentTarget.dataset.type; 

      // console.log('Filter option changed:', type, e.currentTarget.checked);
      dispatchFilterSettings({ type: `SET_${type.toUpperCase()}`, [type]: e.currentTarget.checked });
    }

    setSearching(true);
    setPage(0);
    setPagination(null);
    setSearch(true);
  }

  function handleModalFilterOptions(e) {
    if (searching) return;

    let isModal = false;

    if (e) {
      const type = e.currentTarget.dataset.type;
      isModal = e.currentTarget.dataset.modal === 'true';


      if (type === 'radius-mobile') {
        // console.log('Radius changed - Mobile:', e.currentTarget.dataset.code);
        setModalRadius(parseInt(e.currentTarget.dataset.code) || 25);
      } else {
        // console.log('Filter option changed:', type, e.currentTarget.checked);
        dispatchModalFilterSettings({ type: `SET_${type.toUpperCase()}`, [type]: e.currentTarget.checked });
      }
    }
  }

  useEffect(() => {
    if (search) {
      setSearch(false);
      setErrorMessage(initErrorMessage);

      fetchResults();
    } else {
      const { current: resultsElement } = resultsRef;
      if (resultsElement && page === 1 && results.length > 0) {
        resultsElement.scrollIntoView({ behavior: 'smooth' });
      }
      resetDropdownFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page]);

  useEffect(() => {
    resetDropdownFilter();
    // console.log('Radius changed:', radius);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius]);

  useEffect(() => {
    function handleResize() {
      window.innerWidth >= 700 && modalRef.current?.close();
    }

    handleResize();

    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const finalRadius = useMemo(() => {
    return pagination?.overrideFilters ? pagination?.overrideRadius : pagination?.r;
  }, [pagination]);

  return (
    <>
      <section className={`find-doctor-tool ${isHcp ? 'find-doctor-tool--hcp' : ''}`} id="search" ref={searchRef}>
        <div className="container">
          <form noValidate>
            <div className="find-doctor-tool__wrapper">
              <div className="find-doctor-tool__content-body">
                {children}
              </div>
              <div className="find-doctor-tool__content">
                <div className="find-doctor-tool__form-group" ref={formRef}>
                  <FormInputText
                    ref={zipCodeRef}
                    name="zip_code"
                    label="ZIP code"
                    placeholder="ZIP code (#####)"
                    required={true}
                    autoformatNumericOnly
                    disableRequiredAsterisk
                    errorMessage={errorMessage}
                    type="zip"
                    maxLength={10}
                    // @ts-ignore
                    onChange={() => {
                      !zipCodeRef.current?.isValid && zipCodeRef.current?.setValid(true);
                      !zipCodeRef.current?.isValid && setErrorMessage(initErrorMessage);
                      if (pagination && pagination?.overrideFilters) {
                        setPagination(null);
                        setPage(0);
                        resetDropdownFilter();
                        setResults(null);
                      }
                    }}
                    disabled={searching}
                    data-gtm-event-category="Find a Doctor Tool"
                    data-gtm-event="Click"
                    data-gtm-event-label="Zip Code"
                  />

                  {/* <FormInputDropdown
                    ref={radiusRef}
                    name="radius"
                    label="Radius"
                    placeholder="Select radius"
                    omitFirstItem
                    disableFirstItem={false}
                    itemList={radiusOptions}
                    onChange={() => setRadius(parseInt(radiusRef.current?.getCode()) || 25)}
                    disabled={searching || (pagination && pagination?.overrideFilters)}
                  /> */}

                  <Button
                    onClick={handleSearch}
                    type="button"
                    disabled={searching}
                    variant={['outline', 'solid--eggplant', 'dtc-big']}
                    iconType={searching ? 'loading' : undefined}
                    data-gtm-event-category="Find a Doctor Tool"
                    data-gtm-event-action="Click"
                    data-gtm-event-label="Find a Doctor Search Button"
                  >
                    Find a doctor
                  </Button>
                </div>
              </div>
              <picture>
                <source
                  srcSet="/images/components/find-doctor-tool/Find-A-Doctor_CAH-Specialist-Mobile.png"
                  media="(max-width: 1023px)"
                  width={225}
                  height={248}
                />
                <CustomImage
                  src="/images/components/find-doctor-tool/Find-A-Doctor_CAH-Specialist-Desktop.png"
                  alt=""
                  width={516}
                  height={533}
                />
                <span>Actor portrayal.</span>
              </picture>
            </div>
          </form>
          <div className="find-doctor-tool__disclaimer">
            {isHcp && <p className="!text-[12px] max-w-[580px]">If you are a healthcare provider and would like to be included in this directory, removed from this directory, or update your information, please <Button
              customClass="no-wrap !underline !text-[12px]"
              onClick={() => directoryModalRef.current?.open()}
            >click here</Button>.</p>}
            {!isHcp && <p className="!text-[12px]">
              If you are a healthcare professional, please visit the <Button
                externalLink
                href="/hcp"
                data-gtm-event-category="External Link"
                data-gtm-event-action="Click"
                data-gtm-event-label="HCP site"
              >HCP site</Button>.
            </p>}
            <p>{
              isHcp
                ? <>
                    <span className="hang">*</span>
                    This directory is for educational and informational purposes only and is provided to help you find a healthcare professional in your area that may have experience with CAH. No fees have been received by or paid to healthcare professionals for inclusion in this directory. Inclusion of a healthcare professional in this directory does not represent an endorsement by or a recommendation from Neurocrine, nor does it imply that the healthcare professional will determine that a Neurocrine product is right for your needs. Neurocrine makes no warranty as to the credentials, skill, or outcomes of the listed professional. You are ultimately responsible for the selection of a healthcare professional for your needs. This directory is just one source of information available to you.
                  </>
                : <>
                    <span className="hang">*</span>
                    This directory is for educational and informational purposes only and is provided to help you find a healthcare professional in your area that may have experience with CAH. No fees have been received by or paid to healthcare professionals for inclusion in this directory. Inclusion of a healthcare professional in this directory does not represent an endorsement by or a recommendation from Neurocrine, nor does it imply that the healthcare professional will determine that a Neurocrine product is right for your needs. Neurocrine makes no warranty as to the credentials, skill, or outcomes of the listed professional. You are ultimately responsible for the selection of a healthcare professional for your needs. This directory is just one source of information available to you.
                  </>
              }</p>
          </div>
        </div>
      </section>
      {results && <section className={`find-doctor-tool ${isHcp ? 'find-doctor-tool--hcp' : ''}`} ref={resultsRef}>
        <div className="container">
          <div className="find-doctor-tool__search-results">
            {instructions && <p className="find-doctor-tool__search-results-instructions">
              {instructions}
            </p>}
            <p className="find-doctor-tool__search-results-header">
              {!searching
                ? <>Showing <span>{results.length}</span> of <span>{pagination?.total}</span> results <br className="mobile:hidden" />within <span>{finalRadius}</span> miles of <a href="#search"><span>{pagination?.zip_code}</span></a></>
                : <>Searching...</>
              }
            </p>
            {((pagination && pagination?.overrideFilters) || (pagination && pagination?.nearestRadius)) && <p className="find-doctor-tool__search-results-filters-info">
              Radius has been adjusted to reach {!isHcp ? 'doctors' : 'physicians'} nearest you. <Button customClass="!underline" onClick={(e) => {
                e.preventDefault();

                if (pagination && pagination?.overrideFilters) {
                  dispatchFilterSettings({ type: 'RESET' });
                  setResults(null);
                  setPage(0);
                  setPagination(null);
                  setRadius(25);
                  setAutoScrollActive(true);

                  setTimeout(() => {
                    const rect = window.innerWidth >= 1024 ? searchRef.current?.getBoundingClientRect() : formRef.current?.getBoundingClientRect();
                    const y = rect.top + window.scrollY;
                    window.scrollTo({top: y, behavior: "smooth"});
                  }, 333);
                } else {
                  handleSearch(e);
                }
              }}
                data-gtm-event-category="Find a Doctor Tool"
                data-gtm-event="Click"
                data-gtm-event-label={finalRadius > 200 ? 'Reset search' : 'Reset filters'}
              >{finalRadius > 200 ? 'Reset search' : 'Reset filters'}</Button> to see more providers.
            </p>}
            <form noValidate>
              <div className="find-doctor-tool__search-results-filters-mobile">
                <Button
                  type="button"
                  disabled={searching || (pagination && pagination?.overrideFilters)}
                  onClick={() => {
                    dispatchModalFilterSettings({ type: `SET`, ...filterSettings });
                    setModalRadius(radius);
                    modalRef.current?.open()}
                  }
                  data-gtm-event-category="Find a Doctor Tool"
                  data-gtm-event="Click"
                  data-gtm-event-label="Open Filter Modal"
                >
                  <span>Filter Results&nbsp;&nbsp;<span className="find-doctor-tool__filter-count">{numSelectedFilters}</span></span>
                </Button>
              </div>
              <div className="find-doctor-tool__search-results-filters">
                <FormInputDropdown
                  name="filter-radius"
                  data-filter="radius"
                  label={null}
                  itemList={filterRadiusOptions}
                  ref={filterRadiusRef}
                  omitFirstItem
                  disableFirstItem={false}
                  placeholder="Select radius"
                  onChange={() => setRadius(parseInt(filterRadiusRef.current?.getCode()) || 25)}
                  // @ts-ignore
                  onClick={() => {
                    setTimeout(() => {
                      setSearching(true);
                      setPage(0);
                      setPagination(null);
                      setSearch(true);
                    }, 150);
                  }}
                  disabled={searching || (pagination && pagination?.overrideFilters)}
                  data-gtm-event-category="Find a Doctor Tool"
                  data-gtm-event="Click"
                  data-gtm-event-label="Select Radius"
                />
                <FormInputCheckbox
                  data-type="publications"
                  ref={(el) => (hasPublicationsRef.current[0] = el)}
                  name="has-publications"
                  disabled={searching || (pagination && pagination?.overrideFilters)}
                  onClick={handleFilterOptions}
                  // @ts-ignore
                  checked={filterSettings.publications}
                  data-gtm-event-category="Find a Doctor Tool"
                  data-gtm-event="Click"
                  data-gtm-event-label="Publications"
                >
                  Publications
                </FormInputCheckbox>
                <FormInputCheckbox
                  data-type="clinical_trials"
                  ref={(el) => (hasClinicalTrialsRef.current[0] = el)}
                  name="has-clinical-trials"
                  disabled={searching || (pagination && pagination?.overrideFilters)}
                  onClick={handleFilterOptions}
                  // @ts-ignore
                  checked={filterSettings.clinical_trials}
                  data-gtm-event-category="Find a Doctor Tool"
                  data-gtm-event="Click"
                  data-gtm-event-label="Clinical Trials"
                >
                  Clinical trials
                </FormInputCheckbox>
                <FormInputCheckbox
                  data-type="ccc_coe"
                  ref={(el) => (practiceCccCoeRef.current[0] = el)}
                  name="practice-ccc-coe"
                  disabled={searching || (pagination && pagination?.overrideFilters)}
                  onClick={handleFilterOptions}
                  // @ts-ignore
                  checked={filterSettings.ccc_coe}
                  data-gtm-event-category="Find a Doctor Tool"
                  data-gtm-event="Click"
                  data-gtm-event-label="Comprehensive practice"
                >
                  Comprehensive practice 
                </FormInputCheckbox>
              </div>
            </form>
            <div className={`find-doctor-tool__search-results-list ${searching ? 'find-doctor-tool__search-results-list--searching' : ''}`.trim()}>
              {results.map((doctor, index) => (
                <FindDoctorCard key={index} doctor={doctor} />
              ))}
              {Array.from({ length: 3 - (results.length % 3) < 3 ? 3 - (results.length % 3) : 0 }, (_, i) => (i)).map((_, i) => (
                <div key={i} className="find-doctor-tool__card-filler" />
              ))}
              {!searching && results.length === 0 && Array.from({ length: 6 }).map((_, index) => (
                <FindDoctorCard key={index} doctor={{}} customClass="find-doctor-tool__card--no-results" />
              ))}
            </div>
            <div className="find-doctor-tool__bottom">
              {pagination && (<div className="find-doctor-tool__pagination" ref={bottomRef}>
                {results.length < pagination.total && <Button
                  type="button"
                  disabled={searching}
                  data-direction="load-more"
                  onClick={handlePagination}
                  customClass={`find-doctor-tool__load-more ${searching ? 'find-doctor-tool__load-more--searching' : ''}`.trim()}
                  data-gtm-event-category="Find a Doctor Tool"
                  data-gtm-event-action="Click"
                  data-gtm-event-label="Load More Results Button"
                >
                  Load More Results
                </Button>}
              </div>)}
            </div>
          </div>
        </div>
      </section>}
      <Modal ref={modalRef} customClass="find-doctor-tool__modal">
        <div className="find-doctor-tool__modal__header">Radius</div>
        {radiusOptions.map(({label, itemLabel, code}, index) => (
          <div key={index} className={`find-doctor-tool__modal__item ${index === 0 ? 'find-doctor-tool__modal__item--first' : ''} ${index === radiusOptions.length - 1 ? 'find-doctor-tool__modal__item--last' : ''}`.trim()}>
            <FormInputCheckbox
              radioGroupName="radius-mobile-group"
              typeRadio
              data-type="radius-mobile"
              data-modal={true}
              data-code={code}
              // @ts-ignore
              id={"radius-mobile-" + code}
              disabled={searching || (pagination && pagination?.overrideFilters)}
              onClick={handleModalFilterOptions}
              // @ts-ignore
              defaultChecked={code.toString() === radius?.toString()}
              data-gtm-event-category="Find a Doctor Tool"
              data-gtm-event="Click"
              data-gtm-event-label={`Select Radius - ${itemLabel}`}
            >
              {label}
            </FormInputCheckbox>
          </div>
        ))}
        <div className="find-doctor-tool__modal__header">More filters</div>
        <div className="find-doctor-tool__modal__item find-doctor-tool__modal__item--first">
          <FormInputCheckbox
            data-type="publications"
            data-modal={true}
            ref={(el) => (hasPublicationsRef.current[1] = el)}
            name="has-publications-mobile"
            disabled={searching || (pagination && pagination?.overrideFilters)}
            onClick={handleModalFilterOptions}
            // @ts-ignore
            defaultChecked={filterSettings.publications}
            data-gtm-event-category="Find a Doctor Tool"
            data-gtm-event="Click"
            data-gtm-event-label="Publications"
          >
            Publications
          </FormInputCheckbox>
        </div>
        <div className="find-doctor-tool__modal__item">
          <FormInputCheckbox
            data-type="clinical_trials"
            data-modal={true}
            ref={(el) => (hasClinicalTrialsRef.current[1] = el)}
            name="has-clinical-trials-mobile"
            disabled={searching || (pagination && pagination?.overrideFilters)}
            onClick={handleModalFilterOptions}
            // @ts-ignore
            defaultChecked={filterSettings.clinical_trials}
            data-gtm-event-category="Find a Doctor Tool"
            data-gtm-event="Click"
            data-gtm-event-label="Clinical trials"
          >
            Clinical trials
          </FormInputCheckbox>
        </div>
        <div className="find-doctor-tool__modal__item find-doctor-tool__modal__item--last">
          <FormInputCheckbox
            data-type="ccc_coe"
            data-modal={true}
            ref={(el) => (practiceCccCoeRef.current[1] = el)}
            name="practice-ccc-coe-mobile"
            disabled={searching || (pagination && pagination?.overrideFilters)}
            onClick={handleModalFilterOptions}
            // @ts-ignore
            defaultChecked={filterSettings.ccc_coe}
            data-gtm-event-category="Find a Doctor Tool"
            data-gtm-event="Click"
            data-gtm-event-label="Comprehensive practice"
          >
            Comprehensive practice
          </FormInputCheckbox>
        </div>
        <div className="find-doctor-tool__modal__header">
          <Button
            type="button"
            variant={['solid', 'solid--eggplant', 'dtc-big']}
            onClick={() => {
              modalRef.current?.close();

              setRadius(modalRadius)
              dispatchFilterSettings({ type: `SET`, ...modalFilterSettings });

              setSearching(true);
              setPage(0);
              setPagination(null);
              setSearch(true);
            }}
            data-gtm-event-category="Find a Doctor Tool"
            data-gtm-event="Click"
            data-gtm-event-label="Submit"
          >
            Submit
          </Button>
        </div>
      </Modal>
      <Modal ref={directoryModalRef} customClass="find-doctor-tool__directory-modal">
        <DoctorFinderDirectoryModal directoryModalRef={directoryModalRef} thankYouModalRef={thankYouModalRef} />
      </Modal>
      <Modal ref={thankYouModalRef} customClass="find-doctor-tool__thank-you-modal">
        <p className="h1">Thank you!</p>
        <p>You&rsquo;re all set. Your information is with our team for review.</p>
      </Modal>
    </>
  );
}